import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown } from "lucide-react";
import { SPOTIFY_ALBUMS } from "../data/content";

const Equalizer = () => (
  <div className="flex h-6 items-end gap-[3px]">
    {[0, 0.2, 0.4, 0.15, 0.3].map((d, i) => (
      <span key={i} className="eq-bar" style={{ animationDelay: `${d}s` }} />
    ))}
  </div>
);

export const FloatingPlayer = () => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [album, setAlbum] = useState(0);

  useEffect(() => {
    const onPlay = (e) => {
      const idx = typeof e.detail === "number" ? e.detail : 0;
      setAlbum(idx);
      setOpen(true);
      setMinimized(false);
    };
    window.addEventListener("oli-play", onPlay);
    return () => window.removeEventListener("oli-play", onPlay);
  }, []);

  if (!open) return null;
  const current = SPOTIFY_ALBUMS[album];

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-3 pb-3 sm:px-4 sm:pb-4">
      <AnimatePresence mode="wait">
        {minimized ? (
          <motion.button
            key="mini"
            data-testid="player-expand"
            onClick={() => setMinimized(false)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="btn-glow ml-auto flex items-center gap-3 rounded-full glass-strong px-5 py-3"
          >
            <Equalizer />
            <span className="font-heading text-sm font-semibold text-white">En lecture</span>
          </motion.button>
        ) : (
          <motion.div
            key="full"
            data-testid="floating-player"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className="glass-strong w-full max-w-3xl overflow-hidden rounded-2xl p-3 shadow-2xl"
            style={{ boxShadow: "0 0 40px rgba(0,71,255,0.25)" }}
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="flex items-center gap-3">
                <Equalizer />
                <span className="font-heading text-xs uppercase tracking-widest text-white/60">
                  Lecteur Oli.Vero
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {SPOTIFY_ALBUMS.map((a, i) => (
                  <button
                    key={a.id}
                    data-testid={`player-album-${i}`}
                    onClick={() => setAlbum(i)}
                    className={`rounded-full px-3 py-1 font-heading text-xs transition-colors ${
                      i === album ? "bg-electric text-white" : "text-white/60 hover:text-white"
                    }`}
                  >
                    {a.title.length > 14 ? a.title.slice(0, 12) + "…" : a.title}
                  </button>
                ))}
                <button
                  data-testid="player-minimize"
                  onClick={() => setMinimized(true)}
                  aria-label="Réduire"
                  className="ml-1 flex h-7 w-7 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <ChevronDown size={16} />
                </button>
                <button
                  data-testid="player-close"
                  onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  className="flex h-7 w-7 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <iframe
              key={current.id}
              title={`Lecteur ${current.title}`}
              src={`https://open.spotify.com/embed/album/${current.id}?theme=0`}
              width="100%"
              height="80"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ borderRadius: 12 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FloatingPlayer;
